import { useEffect, useState, useCallback, useRef } from 'react';
import { HStack, Text, Button } from '@chakra-ui/react';

/**
 * VersionStatus
 *
 * Plain-language "are you current?" indicator for non-technical users (works on
 * phone and desktop). Compares the RUNNING build against the LIVE build by
 * fetching /version.json from the network (no-store, bypassing the HTTP cache
 * AND the service worker), and surfaces a waiting service worker (the
 * "stuck on old code" condition).
 *
 * Shows the short commit hash INLINE (no hover needed) so a phone user can read
 * it aloud, and when stale shows BOTH running and live hashes so the mismatch is
 * visible: e.g. "🔴 Update ready v3.4.abc1234 → def5678".
 *
 * States:
 *   GREEN  "Up to date"   — running matches live, no SW waiting.
 *   RED    "Update ready" — newer build live / SW waiting -> Reload button.
 *   GREY   "Checking…" / "Can't verify"
 *
 * Aug 2026: detecting a mismatch here now TRIGGERS a service-worker update
 * check via window.__checkForUpdate(). Previously detection and application
 * were independent: version.json polling would correctly report a new build
 * while the browser had never re-fetched sw.js, leaving registration.waiting
 * null and the Reload button a silent no-op. Calling update() on mismatch is
 * what makes the red dot and the button agree with each other.
 *
 * Sep 2026: the waiting-worker signal now comes from window.__pwaNeedRefresh,
 * which main.jsx only sets when the running commit is actually behind live.
 * A worker waiting behind already-current code no longer turns the dot red.
 * The flag is read on mount so this component can't miss an event that fired
 * before it mounted (the cause of the banner/dot disagreeing).
 *
 * Requires: VITE_COMMIT_HASH (baked), /version.json (written by vite.config),
 * and window.__applyUpdate() / window.__checkForUpdate() from main.jsx.
 */
export const VersionStatus = () => {
  const running = import.meta.env.VITE_COMMIT_HASH || 'unknown';
  const major = import.meta.env.VITE_MAJOR_VERSION || '';
  const [live, setLive] = useState(null);
  const [state, setState] = useState('checking'); // checking | current | stale | unknown
  const [swWaiting, setSwWaiting] = useState(() => window.__pwaNeedRefresh === true);

  // Only ask the browser to re-fetch sw.js once per newly-seen live hash,
  // rather than on every poll cycle while we sit stale.
  const updateRequestedFor = useRef(null);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setLive(data.commit);

      if (data.commit === running) {
        setState('current');
        updateRequestedFor.current = null;
        return;
      }

      setState('stale');

      // A newer build is live. Make the browser actually go look for the new
      // worker -- without this it may never fetch sw.js at all (long-lived
      // session, sleep/resume, installed PWA window) and the Reload button
      // will have nothing waiting to apply.
      if (updateRequestedFor.current !== data.commit) {
        updateRequestedFor.current = data.commit;
        window.__checkForUpdate?.();
      }
    } catch {
      setState('unknown');
    }
  }, [running]);

  useEffect(() => {
    check();
    if (window.__pwaNeedRefresh) setSwWaiting(true);
    const onFocus = () => check();
    const onWaiting = () => setSwWaiting(true);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pwa:need-refresh', onWaiting);
    const id = setInterval(check, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pwa:need-refresh', onWaiting);
      clearInterval(id);
    };
  }, [check]);

  const outOfDate = swWaiting || state === 'stale';

  const reload = () => {
    if (typeof window.__applyUpdate === 'function') window.__applyUpdate();
    else window.location.reload();
  };

  let dot, label, color;
  if (state === 'checking')      { dot = '⏳'; label = 'Checking…';    color = 'gray.400'; }
  else if (outOfDate)            { dot = '🔴'; label = 'Update ready'; color = 'orange.300'; }
  else if (state === 'unknown')  { dot = '⚪'; label = "Can't verify"; color = 'gray.400'; }
  else                          { dot = '🟢'; label = 'Up to date';   color = 'green.300'; }

  const versionText = `${major ? major + '.' : ''}${running}`;

  return (
    <HStack spacing="2" align="center" px="2" fontSize="xs" flexWrap="wrap">
      <Text>{dot}</Text>
      <Text color={color} fontWeight="medium" whiteSpace="nowrap">{label}</Text>
      <Text color="gray.400" whiteSpace="nowrap">{versionText}</Text>
      {outOfDate && live && (
        <Text color="gray.500" whiteSpace="nowrap">→ {live}</Text>
      )}
      {outOfDate && (
        <Button size="xs" colorScheme="orange" onClick={reload}>
          Reload
        </Button>
      )}
    </HStack>
  );
};
